const DESKTOP_QUERY = '(min-width: 1080px)';
const COPY_FEEDBACK_MS = 2000;

/**
 * Header
 *
 * Drives one markup tree as two menus.
 *
 * Mobile is a drill down: the hamburger opens the drawer, a category panel
 * slides in over the primary list, and a subcategory panel slides in over that.
 * Desktop is a mega menu: a category opens a full width panel in which every
 * group is already open.
 *
 * Nothing here is category specific. Any `[data-menu-toggle]` opens the
 * `[data-menu-panel]` named by its `aria-controls`, at any depth, so new
 * categories and subcategories work without touching this file.
 */
export default class Header {
    constructor() {
        this.root = document.querySelector('[data-header]');

        if (null === this.root) {
            return;
        }

        this.trigger = this.root.querySelector('[data-menu-trigger]');
        this.overlay = this.root.querySelector('[data-menu-overlay]');
        this.searchField = this.root.querySelector('.header__search-field');
        this.loginPanel = this.root.querySelector('[data-login-panel]');
        this.loginField = this.root.querySelector('.header__login-form input');

        this.desktop = window.matchMedia(DESKTOP_QUERY);
        this.isDesktop = null;
        this.measureFrame = null;
        this.copyTimeout = null;

        this.onClick = this.onClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBreakpoint = this.onBreakpoint.bind(this);
        this.requestMeasure = this.requestMeasure.bind(this);

        this.bind();
        this.onBreakpoint();
        this.measure();
    }

    bind() {
        this.root.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.requestMeasure);
        window.addEventListener('scroll', this.requestMeasure, { passive: true });

        if ('function' === typeof this.desktop.addEventListener) {
            this.desktop.addEventListener('change', this.onBreakpoint);
        } else {
            this.desktop.addListener(this.onBreakpoint);
        }

        if ('undefined' !== typeof ResizeObserver) {
            this.observer = new ResizeObserver(this.requestMeasure);
            this.observer.observe(this.root);
        }
    }

    // -----------------------------------------------------------------------
    // Events
    // -----------------------------------------------------------------------

    onClick(event) {
        const back = event.target.closest('[data-menu-back]');

        if (null !== back && true === this.root.contains(back)) {
            event.preventDefault();
            this.setPanel(back.closest('[data-menu-panel]'), false);
            return;
        }

        // Group headings are decorative <span>s on desktop, so only act on a
        // real button.
        const toggle = event.target.closest('[data-menu-toggle]');

        if (null !== toggle && true === this.root.contains(toggle) && 'BUTTON' === toggle.tagName) {
            event.preventDefault();
            this.togglePanel(toggle);
            return;
        }

        if (null !== event.target.closest('[data-menu-trigger]')) {
            event.preventDefault();
            this.setMenu(false === this.root.classList.contains('is-menu-open'));
            return;
        }

        if (null !== event.target.closest('[data-search-toggle]')) {
            event.preventDefault();
            this.setSearch(false === this.root.classList.contains('is-search-open'));
            return;
        }

        if (null !== event.target.closest('[data-search-close]')) {
            event.preventDefault();
            this.setSearch(false);
            return;
        }

        if (null !== event.target.closest('[data-login-toggle]')) {
            event.preventDefault();
            this.setLogin(false === this.root.classList.contains('is-login-open'));
            return;
        }

        const copy = event.target.closest('[data-copy]');

        if (null !== copy) {
            event.preventDefault();
            this.copy(copy);
            return;
        }

        if (event.target === this.overlay) {
            this.closePanels();
        }
    }

    onDocumentClick(event) {
        if (false === this.root.classList.contains('is-login-open')) {
            return;
        }

        if (null !== event.target.closest('[data-login-panel]') || null !== event.target.closest('[data-login-toggle]')) {
            return;
        }

        this.setLogin(false);
    }

    onKeydown(event) {
        if ('Escape' !== event.key) {
            return;
        }

        if (true === this.root.classList.contains('is-search-open')) {
            this.setSearch(false);
            return;
        }

        if (true === this.root.classList.contains('is-login-open')) {
            this.setLogin(false);
            return;
        }

        const open = this.openPanels();

        if (0 < open.length) {
            const deepest = open[open.length - 1];
            const toggle = this.toggleFor(deepest);

            this.setPanel(deepest, false);

            if (null !== toggle && 'BUTTON' === toggle.tagName) {
                toggle.focus({ preventScroll: true });
            }

            return;
        }

        if (true === this.root.classList.contains('is-menu-open')) {
            this.setMenu(false);

            if (null !== this.trigger) {
                this.trigger.focus({ preventScroll: true });
            }
        }
    }

    onBreakpoint() {
        const isDesktop = this.desktop.matches;

        if (isDesktop === this.isDesktop) {
            return;
        }

        this.isDesktop = isDesktop;

        this.closePanels();
        this.setMenu(false);
        this.setSearch(false);
        this.setLogin(false);

        this.swapGroupHeadings(isDesktop);
        this.labelBackButtons(isDesktop);
        this.measure();
    }

    // -----------------------------------------------------------------------
    // Panels
    // -----------------------------------------------------------------------

    togglePanel(toggle) {
        const panel = this.panelFor(toggle);

        if (null === panel) {
            return;
        }

        const isOpen = panel.classList.contains('is-open');

        // Anything open that is neither this panel nor one of its ancestors is
        // a sibling branch, and closes.
        this.openPanels().forEach((open) => {
            if (open === panel || true === open.contains(toggle)) {
                return;
            }

            this.setPanel(open, false);
        });

        this.setPanel(panel, false === isOpen);
    }

    setPanel(panel, open) {
        if (null === panel || undefined === panel) {
            return;
        }

        panel.classList.toggle('is-open', open);

        const toggle = this.toggleFor(panel);

        if (null !== toggle && 'BUTTON' === toggle.tagName) {
            toggle.setAttribute('aria-expanded', true === open ? 'true' : 'false');
        }

        if (false === open) {
            panel.querySelectorAll('[data-menu-panel].is-open').forEach((child) => {
                this.setPanel(child, false);
            });
        }

        // Only a category panel slides in over the list and carries a back
        // control. A group expands in place, so focus stays on its toggle.
        if (true === open && false === this.isDesktop && '1' === panel.dataset.menuLevel) {
            const back = panel.querySelector('[data-menu-back]');

            if (null !== back) {
                back.focus({ preventScroll: true });
            }
        }

        this.syncPanels();
    }

    closePanels() {
        this.openPanels().forEach((panel) => this.setPanel(panel, false));
    }

    syncPanels() {
        const hasOpen = 0 < this.openPanels().length;

        this.root.classList.toggle('is-panel-open', hasOpen);

        if (null !== this.overlay) {
            this.overlay.toggleAttribute('hidden', false === hasOpen);
        }
    }

    openPanels() {
        return Array.from(this.root.querySelectorAll('[data-menu-panel].is-open'));
    }

    panelFor(toggle) {
        const id = toggle.getAttribute('aria-controls') || toggle.getAttribute('data-controls');

        return null === id ? null : this.root.querySelector(`#${CSS.escape(id)}`);
    }

    toggleFor(panel) {
        if (undefined === panel.id || '' === panel.id) {
            return null;
        }

        return this.root.querySelector(`[aria-controls="${panel.id}"]`);
    }

    // -----------------------------------------------------------------------
    // Drawer and search
    // -----------------------------------------------------------------------

    setMenu(open) {
        this.root.classList.toggle('is-menu-open', open);

        if (null !== this.trigger) {
            this.trigger.setAttribute('aria-expanded', true === open ? 'true' : 'false');
        }

        document.documentElement.style.overflow = (true === open && false === this.isDesktop) ? 'hidden' : '';

        if (false === open) {
            this.closePanels();
        } else {
            this.setSearch(false);
            this.setLogin(false);
        }

        this.measure();
    }

    setSearch(open) {
        this.root.classList.toggle('is-search-open', open);

        this.root.querySelectorAll('[data-search-toggle]').forEach((toggle) => {
            toggle.setAttribute('aria-expanded', true === open ? 'true' : 'false');
        });

        if (true === open) {
            this.setLogin(false);

            if (null !== this.searchField) {
                this.searchField.focus({ preventScroll: true });
            }
        }
    }

    setLogin(open) {
        this.root.classList.toggle('is-login-open', open);

        this.root.querySelectorAll('[data-login-toggle]').forEach((toggle) => {
            toggle.setAttribute('aria-expanded', true === open ? 'true' : 'false');
        });

        if (null !== this.loginPanel) {
            this.loginPanel.toggleAttribute('hidden', false === open);
        }

        if (true === open) {
            this.setSearch(false);

            if (null !== this.loginField) {
                this.loginField.focus({ preventScroll: true });
            }
        }

        // In flow on mobile, so opening or closing it moves the header's
        // bottom edge and the drawer offset that tracks it.
        this.measure();
    }

    // -----------------------------------------------------------------------
    // Breakpoint adjustments
    // -----------------------------------------------------------------------

    /**
     * Second level headings are <button>s on mobile, where they open a sliding
     * panel. On desktop those panels are always open, so the heading is not a
     * control and becomes a decorative <span>.
     */
    swapGroupHeadings(isDesktop) {
        const wanted = true === isDesktop ? 'span' : 'button';

        this.root.querySelectorAll('[data-menu-toggle="group"]').forEach((element) => {
            if (wanted === element.tagName.toLowerCase()) {
                return;
            }

            const replacement = document.createElement(wanted);
            const controls = element.getAttribute('aria-controls') || element.getAttribute('data-controls');

            replacement.className = element.className;
            replacement.innerHTML = element.innerHTML;
            replacement.setAttribute('data-menu-toggle', 'group');
            replacement.setAttribute('data-desktop-tag', 'span');

            if (null !== controls) {
                if (true === isDesktop) {
                    // Stashed so the button can be rebuilt on the way back.
                    replacement.setAttribute('data-controls', controls);
                } else {
                    replacement.setAttribute('aria-controls', controls);
                    replacement.setAttribute('aria-expanded', 'false');
                }
            }

            if (false === isDesktop) {
                replacement.setAttribute('type', 'button');
            }

            element.replaceWith(replacement);
        });
    }

    /**
     * The same button is a "back to Accounts" control on mobile and the panel
     * close control on desktop.
     */
    labelBackButtons(isDesktop) {
        this.root.querySelectorAll('[data-menu-back]').forEach((back) => {
            if (true === isDesktop) {
                back.setAttribute('aria-label', 'Close menu');
            } else {
                back.removeAttribute('aria-label');
            }
        });
    }

    // -----------------------------------------------------------------------
    // Measurement
    // -----------------------------------------------------------------------

    requestMeasure() {
        if (null !== this.measureFrame) {
            return;
        }

        this.measureFrame = window.requestAnimationFrame(() => {
            this.measureFrame = null;
            this.measure();
        });
    }

    /**
     * The mobile drawer is fixed below the header, so it needs to know how far
     * down the header actually ends - banner, utility bar and all.
     */
    measure() {
        const bottom = Math.max(0, Math.round(this.root.getBoundingClientRect().bottom));

        this.root.style.setProperty('--header-offset', `${bottom}px`);
    }

    // -----------------------------------------------------------------------
    // Routing number
    // -----------------------------------------------------------------------

    copy(button) {
        const value = button.getAttribute('data-copy');
        const status = this.root.querySelector('[data-copy-status]');

        if (null === value || '' === value) {
            return;
        }

        const done = () => {
            button.classList.add('is-copied');

            if (null !== status) {
                status.textContent = 'Routing number copied';
            }

            window.clearTimeout(this.copyTimeout);

            this.copyTimeout = window.setTimeout(() => {
                button.classList.remove('is-copied');

                if (null !== status) {
                    status.textContent = '';
                }
            }, COPY_FEEDBACK_MS);
        };

        if (undefined !== navigator.clipboard && 'function' === typeof navigator.clipboard.writeText) {
            navigator.clipboard.writeText(value).then(done).catch(() => {});
            return;
        }

        const field = document.createElement('textarea');

        field.value = value;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';

        document.body.appendChild(field);
        field.select();

        try {
            document.execCommand('copy');
            done();
        } catch (error) {
            // Clipboard is unavailable; the number is still readable on screen.
        }

        document.body.removeChild(field);
    }
}
